<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useLocale } from 'shared/composables/useLocale';

import ButtonV4 from 'next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  healthData: {
    type: Object,
    default: null,
  },
  healthError: {
    type: Object,
    default: null,
  },
  isEmbeddedSignup: {
    type: Boolean,
    default: false,
  },
  isRegisteringWebhook: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['registerWebhook', 'goToConfiguration']);

const { t, te } = useI18n();
const { resolvedLocale } = useLocale();

const QUALITY_COLORS = {
  GREEN: 'text-n-teal-11',
  YELLOW: 'text-n-amber-11',
  RED: 'text-n-ruby-11',
  UNKNOWN: 'text-n-slate-12',
};

const STATUS_COLORS = {
  APPROVED: 'text-n-teal-11',
  CONNECTED: 'text-n-teal-11',
  VERIFIED: 'text-n-teal-11',
  STANDARD: 'text-n-teal-11',
  ACTIVE: 'text-n-teal-11',
  PENDING_REVIEW: 'text-n-amber-11',
  AVAILABLE_WITHOUT_REVIEW: 'text-n-slate-11',
  EXPIRED: 'text-n-amber-11',
  FLAGGED: 'text-n-amber-11',
  RESTRICTED: 'text-n-ruby-9',
  BANNED: 'text-n-ruby-9',
  DISCONNECTED: 'text-n-ruby-9',
  REJECTED: 'text-n-ruby-9',
  DECLINED: 'text-n-ruby-9',
};

const MODE_COLORS = {
  LIVE: 'text-n-teal-11',
  SANDBOX: 'text-n-slate-11',
};

const formatStatusDisplay = status => {
  const translationKey = `INBOX_MGMT.ACCOUNT_HEALTH.VALUES.STATUSES.${status}`;

  if (te(translationKey)) {
    return t(translationKey);
  }

  return status
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const STATUSES = [
  'APPROVED',
  'AVAILABLE_WITHOUT_REVIEW',
  'PENDING_REVIEW',
  'REJECTED',
  'DECLINED',
  'EXPIRED',
];

const getDisplayNameStatusDescription = status =>
  t(
    `INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_NAME_STATUS.DESCRIPTIONS.${
      STATUSES.includes(status) ? status : 'UNKNOWN'
    }`
  );

const formatDateTime = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(resolvedLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const businessProfile = computed(
  () => props.healthData?.business_profile || null
);

const businessProfileName = computed(
  () =>
    props.healthData?.verified_name ||
    props.healthData?.display_phone_number ||
    t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.NOT_AVAILABLE')
);

const businessProfileDetails = computed(() => {
  if (!businessProfile.value) return [];

  const notAvailable = t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.NOT_AVAILABLE');
  const websites = businessProfile.value.websites?.filter(Boolean) || [];

  return [
    {
      key: 'description',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_DESCRIPTION.LABEL'),
      value: businessProfile.value.description || notAvailable,
      tooltip: t(
        'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_DESCRIPTION.TOOLTIP'
      ),
      wide: true,
    },
    {
      key: 'address',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_ADDRESS.LABEL'),
      value: businessProfile.value.address || notAvailable,
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_ADDRESS.TOOLTIP'),
    },
    {
      key: 'email',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_EMAIL.LABEL'),
      value: businessProfile.value.email || notAvailable,
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_EMAIL.TOOLTIP'),
    },
    {
      key: 'websites',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_WEBSITES.LABEL'),
      value: websites.length ? websites.join('\n') : notAvailable,
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_WEBSITES.TOOLTIP'),
      wide: true,
    },
  ];
});

const healthSections = computed(() => {
  if (!props.healthData) {
    return [];
  }

  const {
    id: phoneNumberId,
    display_phone_number: displayPhoneNumber,
    verified_name: verifiedName,
    name_status: nameStatus,
    quality_rating: qualityRating,
    messaging_limit_tier: messagingLimitTier,
    account_mode: accountMode,
    status: phoneNumberStatus,
    code_verification_status: codeVerificationStatus,
    throughput_level: throughputLevel,
    last_onboarded_time: lastOnboardedTime,
    is_on_biz_app: isOnBizApp,
    platform_type: platformType,
    business_account_id: businessAccountId,
    business_account_name: businessAccountName,
    business_portfolio_id: businessPortfolioId,
    business_portfolio_name: businessPortfolioName,
  } = props.healthData;

  const notAvailable = t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.NOT_AVAILABLE');

  return [
    {
      key: 'phoneNumber',
      title: t('INBOX_MGMT.ACCOUNT_HEALTH.SECTIONS.PHONE_NUMBER'),
      items: [
        {
          key: 'displayPhoneNumber',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_PHONE_NUMBER.LABEL'
          ),
          value: displayPhoneNumber || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_PHONE_NUMBER.TOOLTIP'
          ),
        },
        {
          key: 'verifiedName',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.VERIFIED_NAME.LABEL'),
          value: verifiedName || notAvailable,
          tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.VERIFIED_NAME.TOOLTIP'),
        },
        {
          key: 'displayNameStatus',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_NAME_STATUS.LABEL'
          ),
          value: nameStatus || 'UNKNOWN',
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_NAME_STATUS.TOOLTIP'
          ),
          description: getDisplayNameStatusDescription(nameStatus || 'UNKNOWN'),
          type: 'status',
          wide: true,
        },
        {
          key: 'phoneNumberId',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PHONE_NUMBER_ID.LABEL'),
          value: phoneNumberId || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PHONE_NUMBER_ID.TOOLTIP'
          ),
        },
        {
          key: 'coexistence',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.COEXISTENCE.LABEL'),
          value: 'ACTIVE',
          tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.COEXISTENCE.TOOLTIP'),
          type: 'status',
          show: isOnBizApp === true && platformType === 'CLOUD_API',
        },
        {
          key: 'lastOnboardedTime',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.LAST_ONBOARDED_TIME.LABEL'
          ),
          value: lastOnboardedTime
            ? formatDateTime(lastOnboardedTime)
            : notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.LAST_ONBOARDED_TIME.TOOLTIP'
          ),
          show: Boolean(lastOnboardedTime),
        },
      ],
    },
    {
      key: 'healthAndCapacity',
      title: t('INBOX_MGMT.ACCOUNT_HEALTH.SECTIONS.HEALTH_AND_CAPACITY'),
      items: [
        {
          key: 'phoneNumberStatus',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PHONE_NUMBER_STATUS.LABEL'
          ),
          value: phoneNumberStatus || 'UNKNOWN',
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PHONE_NUMBER_STATUS.TOOLTIP'
          ),
          type: 'status',
        },
        {
          key: 'qualityRating',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.QUALITY_RATING.LABEL'),
          value: qualityRating || 'UNKNOWN',
          tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.QUALITY_RATING.TOOLTIP'),
          type: 'quality',
        },
        {
          key: 'messagingLimitTier',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.MESSAGING_LIMIT_TIER.LABEL'
          ),
          value: messagingLimitTier || 'UNKNOWN',
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.MESSAGING_LIMIT_TIER.TOOLTIP'
          ),
          description: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.MESSAGING_LIMIT_TIER.DESCRIPTION'
          ),
          type: 'tier',
        },
        {
          key: 'accountMode',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.ACCOUNT_MODE.LABEL'),
          value: accountMode || 'UNKNOWN',
          tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.ACCOUNT_MODE.TOOLTIP'),
          type: 'mode',
        },
        {
          key: 'codeVerificationStatus',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.CODE_VERIFICATION_STATUS.LABEL'
          ),
          value: codeVerificationStatus || 'UNKNOWN',
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.CODE_VERIFICATION_STATUS.TOOLTIP'
          ),
          type: 'status',
        },
        {
          key: 'throughputLevel',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.THROUGHPUT_LEVEL.LABEL'),
          value: throughputLevel || 'UNKNOWN',
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.THROUGHPUT_LEVEL.TOOLTIP'
          ),
          type: 'status',
        },
      ],
    },
    {
      key: 'businessAccount',
      title: t('INBOX_MGMT.ACCOUNT_HEALTH.SECTIONS.BUSINESS_ACCOUNT'),
      items: [
        {
          key: 'businessAccountName',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_ACCOUNT_NAME.LABEL'
          ),
          value: businessAccountName || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_ACCOUNT_NAME.TOOLTIP'
          ),
        },
        {
          key: 'businessAccountId',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_ACCOUNT_ID.LABEL'
          ),
          value: businessAccountId || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_ACCOUNT_ID.TOOLTIP'
          ),
        },
        {
          key: 'businessPortfolioName',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_PORTFOLIO_NAME.LABEL'
          ),
          value: businessPortfolioName || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_PORTFOLIO_NAME.TOOLTIP'
          ),
        },
        {
          key: 'businessPortfolioId',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_PORTFOLIO_ID.LABEL'
          ),
          value: businessPortfolioId || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_PORTFOLIO_ID.TOOLTIP'
          ),
        },
      ],
    },
  ].map(section => ({
    ...section,
    items: section.items.filter(item => item.show !== false),
  }));
});

const errorState = computed(() => {
  if (!props.healthError) return null;

  if (props.healthError.type === 'authorization') {
    return {
      title: props.isEmbeddedSignup
        ? t('INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.AUTHORIZATION.EMBEDDED_TITLE')
        : t('INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.AUTHORIZATION.MANUAL_TITLE'),
      description: props.isEmbeddedSignup
        ? t(
            'INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.AUTHORIZATION.EMBEDDED_DESCRIPTION'
          )
        : t(
            'INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.AUTHORIZATION.MANUAL_DESCRIPTION'
          ),
      showConfigurationAction: true,
    };
  }

  return {
    title: t('INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.GENERIC_TITLE'),
    description: t('INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.GENERIC_DESCRIPTION'),
    showConfigurationAction: false,
  };
});

const handleGoToSettings = () => {
  const {
    business_account_id: businessAccountId,
    business_portfolio_id: businessPortfolioId,
  } = props.healthData || {};

  if (businessPortfolioId && businessAccountId) {
    const whatsappBusinessUrl = new URL(
      'https://business.facebook.com/latest/whatsapp_manager/phone_numbers/'
    );
    whatsappBusinessUrl.searchParams.set('business_id', businessPortfolioId);
    whatsappBusinessUrl.searchParams.set('asset_id', businessAccountId);
    window.open(whatsappBusinessUrl.toString(), '_blank');
  } else {
    const fallbackUrl = 'https://business.facebook.com/';
    window.open(fallbackUrl, '_blank');
  }
};

const getQualityRatingTextColor = rating =>
  QUALITY_COLORS[rating] || QUALITY_COLORS.UNKNOWN;

const formatTierDisplay = tier => {
  const translationKey = `INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.${tier}`;

  return te(translationKey) ? t(translationKey) : formatStatusDisplay(tier);
};

const formatModeDisplay = mode => {
  const translationKey = `INBOX_MGMT.ACCOUNT_HEALTH.VALUES.MODES.${mode}`;

  return te(translationKey) ? t(translationKey) : formatStatusDisplay(mode);
};

const getModeStatusTextColor = mode => MODE_COLORS[mode] || 'text-n-slate-12';

const getStatusTextColor = status => STATUS_COLORS[status] || 'text-n-slate-12';

const showWebhookSection = computed(
  () => props.healthData?.webhook_configuration !== undefined
);

// Phone-level override takes precedence over WABA-level (application), so prefer it.
const webhookUrl = computed(
  () =>
    props.healthData?.webhook_configuration?.phone_number ||
    props.healthData?.webhook_configuration?.whatsapp_business_account ||
    props.healthData?.webhook_configuration?.application
);

const webhookConfigured = computed(() => !!webhookUrl.value);

const webhookUrlMismatch = computed(
  () =>
    webhookConfigured.value &&
    webhookUrl.value !== props.healthData?.expected_webhook_url
);

// Whether anything on this screen is an answer from Meta about where delivery is going right now.
// A read that failed leaves the previous payload in place, and every line built from it is a claim
// about a reading that did not happen.
const hasReading = computed(
  () => Boolean(props.healthData) && !errorState.value
);

// Registering a webhook needs the channel's `provider_config`, which the endpoint already has, and
// never the health payload, so this control is not part of the card's state.
//
// A failed read is the case the issue is about: it says nothing about the wiring, and that is
// exactly when the wiring is the most plausible thing to repair, so the control stays. The one
// state that must stay quiet is "nothing is known yet", which is a different condition from "the
// read failed": no data and no error.
const showRegisterWebhook = computed(() => {
  if (errorState.value) return true;
  if (!props.healthData) return false;

  return !webhookConfigured.value || webhookUrlMismatch.value;
});

const showWebhookCard = computed(
  () =>
    (hasReading.value && showWebhookSection.value) || showRegisterWebhook.value
);

// A number with no override of its own rides on the app's own callback, which belongs to the
// installation and can be pointed elsewhere at any time. The chip above cannot say that: green
// whenever the effective URL is ours, amber "mismatch" whenever it is not, and both readings are
// the same for a number that owns its routing and one that does not. The reading of Meta's three
// levels is the endpoint's (`routed_by_app_callback_only`), so an API consumer sees what the
// screen sees; what is decided here is which sentence that answer deserves.
const appCallbackRoutingMessage = computed(() => {
  if (props.healthData?.routed_by_app_callback_only !== true) return null;
  // Nothing is configured at all: the chip already says so, and its button is the repair.
  if (!webhookConfigured.value) return null;

  // Mismatch splits into two repairs that the one chip cannot tell apart. With an override of its
  // own, the button above rewrites it and nothing else needs saying. With none, the URL on the
  // card IS the app callback, so the button is writing a per-number override that does not exist
  // yet: it repairs this whenever Meta allows it, and Meta refuses it for a whole class of
  // accounts. Which of the two is only known at the press, and the toast reports it; the sentence
  // is for before that, and it names the app callback as the fallback rather than as the repair.
  return webhookUrlMismatch.value
    ? 'INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.APP_CALLBACK_POINTS_ELSEWHERE'
    : 'INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.RIDES_ON_APP_CALLBACK';
});

const handleRegisterWebhook = () => {
  emit('registerWebhook');
};

const handleGoToConfiguration = () => {
  emit('goToConfiguration');
};

const handleCopyWebhookUrl = async url => {
  await copyTextToClipboard(url);
  useAlert(t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.COPY_SUCCESS'));
};
</script>

<template>
  <div class="gap-4 mx-6">
    <div
      class="px-5 py-5 space-y-6 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2"
    >
      <div
        class="flex flex-col gap-5 justify-between items-start w-full md:flex-row"
      >
        <div>
          <span class="text-heading-3 text-n-slate-12">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.TITLE') }}
          </span>
          <p class="mt-1 text-body-main text-n-slate-11">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.DESCRIPTION') }}
          </p>
        </div>
        <ButtonV4
          sm
          solid
          blue
          class="flex-shrink-0"
          @click="handleGoToSettings"
        >
          {{ t('INBOX_MGMT.ACCOUNT_HEALTH.GO_TO_SETTINGS') }}
        </ButtonV4>
      </div>

      <div v-if="healthData && !errorState" class="space-y-6">
        <section v-if="businessProfile">
          <h3 class="mb-3 text-heading-4 text-n-slate-12">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.SECTIONS.BUSINESS_PROFILE') }}
          </h3>
          <div class="space-y-4">
            <div
              class="flex items-center gap-4 p-4 rounded-lg border border-n-weak bg-n-solid-1"
            >
              <Avatar
                :src="businessProfile.profile_picture_url"
                :name="businessProfileName"
                :size="64"
                rounded-full
              />
              <div class="min-w-0">
                <span
                  class="text-label-small font-medium uppercase tracking-wide text-n-teal-11"
                >
                  {{
                    t(
                      'INBOX_MGMT.ACCOUNT_HEALTH.BUSINESS_PROFILE.PREVIEW_LABEL'
                    )
                  }}
                </span>
                <p class="mt-1 text-heading-4 text-n-slate-12 break-words">
                  {{ businessProfileName }}
                </p>
                <p
                  v-if="healthData.display_phone_number"
                  class="text-body-main text-n-slate-10"
                >
                  {{ healthData.display_phone_number }}
                </p>
                <div class="flex flex-wrap gap-2 items-center mt-2">
                  <span
                    v-if="
                      businessProfile.vertical &&
                      businessProfile.vertical !== 'OTHER'
                    "
                    class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ formatStatusDisplay(businessProfile.vertical) }}
                  </span>
                  <span
                    v-if="businessProfile.about"
                    class="text-label text-n-slate-11 break-words"
                  >
                    {{ businessProfile.about }}
                  </span>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-4 xs:grid-cols-2">
              <div
                v-for="item in businessProfileDetails"
                :key="item.key"
                class="flex flex-col gap-2 p-4 rounded-lg border border-n-weak bg-n-solid-1"
                :class="{ 'xs:col-span-2': item.wide }"
              >
                <div class="flex gap-2 items-center">
                  <span class="text-body-main font-medium text-n-slate-11">
                    {{ item.label }}
                  </span>
                  <Icon
                    v-tooltip.top="item.tooltip"
                    icon="i-lucide-info"
                    class="flex-shrink-0 w-4 h-4 cursor-help text-n-slate-9"
                  />
                </div>
                <span
                  class="text-label whitespace-pre-line break-words text-n-slate-12"
                >
                  {{ item.value }}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section v-for="section in healthSections" :key="section.key">
          <h3 class="mb-3 text-heading-4 text-n-slate-12">
            {{ section.title }}
          </h3>
          <div class="grid grid-cols-1 gap-4 xs:grid-cols-2">
            <div
              v-for="item in section.items"
              :key="item.key"
              class="flex flex-col gap-2 p-4 rounded-lg border border-n-weak bg-n-solid-1"
              :class="{ 'xs:col-span-2': item.wide }"
            >
              <div class="flex gap-2 items-center">
                <span class="text-body-main font-medium text-n-slate-11">
                  {{ item.label }}
                </span>
                <Icon
                  v-tooltip.top="item.tooltip"
                  icon="i-lucide-info"
                  class="flex-shrink-0 w-4 h-4 cursor-help text-n-slate-9"
                />
              </div>
              <div class="flex items-center">
                <span
                  v-if="item.type === 'quality'"
                  class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2"
                  :class="getQualityRatingTextColor(item.value)"
                >
                  {{ item.value }}
                </span>
                <span
                  v-else-if="item.type === 'status'"
                  class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2"
                  :class="getStatusTextColor(item.value)"
                >
                  {{ formatStatusDisplay(item.value) }}
                </span>
                <span
                  v-else-if="item.type === 'mode'"
                  class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2"
                  :class="getModeStatusTextColor(item.value)"
                >
                  {{ formatModeDisplay(item.value) }}
                </span>
                <span
                  v-else-if="item.type === 'tier'"
                  class="text-label text-n-slate-12"
                >
                  {{ formatTierDisplay(item.value) }}
                </span>
                <span v-else class="text-label text-n-slate-12">
                  {{ item.value }}
                </span>
              </div>
              <p
                v-if="item.description"
                class="max-w-3xl text-label-small text-n-slate-10"
              >
                {{ item.description }}
              </p>
            </div>
          </div>
        </section>
      </div>

      <div v-else-if="errorState" class="pt-2">
        <div
          class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div class="flex gap-3 items-start">
            <Icon
              icon="i-lucide-alert-triangle"
              class="flex-shrink-0 mt-0.5 w-5 h-5 text-n-amber-11"
            />
            <div class="min-w-0">
              <h3 class="text-heading-4 text-n-slate-12">
                {{ errorState.title }}
              </h3>
              <p class="mt-1 text-body-main text-n-slate-11">
                {{ errorState.description }}
              </p>
            </div>
          </div>
          <div
            v-if="healthError.message"
            class="p-3 rounded-lg bg-n-alpha-1 text-label-small text-n-slate-11"
          >
            <p class="break-words">{{ healthError.message }}</p>
          </div>
          <div v-if="errorState.showConfigurationAction">
            <ButtonV4 sm solid blue @click="handleGoToConfiguration">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.GO_TO_CONFIGURATION') }}
            </ButtonV4>
          </div>
        </div>
      </div>

      <div v-else class="pt-8">
        <div
          class="flex justify-center items-center p-8 text-center text-n-slate-11"
        >
          <div>
            <Icon icon="i-lucide-activity" class="mb-2 w-8 h-8" />
            <p class="text-body-main text-n-slate-11">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.NO_DATA') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Webhook configuration card. Outside the three states on purpose: registering a webhook
           needs the channel's provider_config, which the endpoint already has, and never the health
           payload. Inside the data branch, the control left the screen exactly when the health read
           failed, which is when the wiring is the most plausible thing to repair (#593).

           What the reading produced stays behind `hasReading`: the chip, the URLs and the app
           callback lines are claims about where delivery is going now, and a read that did not
           answer leaves them describing a moment that has passed. -->
      <div
        v-if="showWebhookCard"
        class="flex flex-col gap-2 p-4 mt-6 rounded-lg border border-n-weak bg-n-solid-1"
      >
        <div class="flex gap-2 items-center">
          <span class="text-body-main font-medium text-n-slate-11">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.TITLE') }}
          </span>
          <Icon
            v-tooltip.top="t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.DESCRIPTION')"
            icon="i-lucide-info"
            class="flex-shrink-0 w-4 h-4 cursor-help text-n-slate-9"
          />
        </div>
        <div
          v-if="hasReading && showWebhookSection"
          class="flex items-center justify-between gap-3"
        >
          <span
            v-if="webhookConfigured && !webhookUrlMismatch"
            class="inline-flex items-center gap-1.5 px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2 text-n-teal-11"
          >
            <Icon icon="i-lucide-check-circle" class="w-3.5 h-3.5" />
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.CONFIGURED_SUCCESS') }}
          </span>
          <span
            v-else
            class="inline-flex items-center gap-1.5 px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2 text-n-amber-11"
          >
            <Icon icon="i-lucide-alert-triangle" class="w-3.5 h-3.5" />
            {{
              webhookUrlMismatch
                ? t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.URL_MISMATCH')
                : t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.ACTION_REQUIRED')
            }}
          </span>
        </div>
        <div
          v-if="hasReading && appCallbackRoutingMessage"
          class="flex gap-1.5 items-start text-label-small text-n-amber-11"
        >
          <Icon
            icon="i-lucide-alert-triangle"
            class="flex-shrink-0 mt-0.5 w-3.5 h-3.5"
          />
          <span>
            {{ t(appCallbackRoutingMessage) }}
          </span>
        </div>
        <div v-if="hasReading && webhookConfigured" class="pt-1 space-y-2">
          <div class="flex items-center gap-3 min-w-0">
            <span class="flex-shrink-0 text-label-small text-n-slate-10">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.CONFIGURED_URL') }}
            </span>
            <span
              v-tooltip.top="webhookUrl"
              class="flex-1 min-w-0 font-mono text-label-small truncate text-n-slate-12"
            >
              {{ webhookUrl }}
            </span>
            <ButtonV4
              v-tooltip.top="t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.COPY_URL')"
              type="button"
              ghost
              sm
              slate
              icon="i-lucide-copy"
              class="flex-shrink-0"
              :aria-label="t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.COPY_URL')"
              @click="handleCopyWebhookUrl(webhookUrl)"
            />
          </div>
          <div
            v-if="webhookUrlMismatch"
            class="flex items-center gap-3 min-w-0"
          >
            <span class="flex-shrink-0 text-label-small text-n-slate-10">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.EXPECTED_URL') }}
            </span>
            <span
              v-tooltip.top="healthData.expected_webhook_url"
              class="flex-1 min-w-0 font-mono text-label-small truncate text-n-slate-12"
            >
              {{ healthData.expected_webhook_url }}
            </span>
            <ButtonV4
              v-tooltip.top="t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.COPY_URL')"
              type="button"
              ghost
              sm
              slate
              icon="i-lucide-copy"
              class="flex-shrink-0"
              :aria-label="t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.COPY_URL')"
              @click="handleCopyWebhookUrl(healthData.expected_webhook_url)"
            />
          </div>
        </div>
        <div v-if="showRegisterWebhook" class="pt-1">
          <ButtonV4
            sm
            solid
            blue
            :is-loading="isRegisteringWebhook"
            :disabled="isRegisteringWebhook"
            @click="handleRegisterWebhook"
          >
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.REGISTER_BUTTON') }}
          </ButtonV4>
        </div>
      </div>
    </div>
  </div>
</template>
